<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  plan: { type: Object, default: null },
  error: { type: String, default: null },
});
const emit = defineEmits(['save', 'close']);
const { t } = useI18n();

const dialogRef = ref(null);

const form = ref({
  name: props.plan?.name || '',
  valid_from: props.plan?.valid_from || '',
  valid_to: props.plan?.valid_to || '',
  repeat_yearly: props.plan?.repeat_yearly || false,
  is_default: props.plan?.is_default || false,
});

const isEditing = computed(() => !!props.plan?.id);
const isValid = computed(() => !!form.value.name.trim());

const handleSubmit = () => {
  if (!isValid.value) return;
  const data = { ...form.value };
  if (!data.valid_from) data.valid_from = null;
  if (!data.valid_to) data.valid_to = null;
  emit('save', data);
};

onMounted(() => dialogRef.value?.open());
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEditing
        ? t('PRICING.PLAN_DIALOG.EDIT_TITLE')
        : t('PRICING.PLAN_DIALOG.ADD_TITLE')
    "
    :confirm-button-label="
      isEditing
        ? t('PRICING.PLAN_DIALOG.UPDATE_BUTTON')
        : t('PRICING.PLAN_DIALOG.ADD_BUTTON')
    "
    :cancel-button-label="t('PRICING.CANCEL')"
    :disable-confirm-button="!isValid"
    @confirm="handleSubmit"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <!-- Name -->
      <div>
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          {{ t('PRICING.PLAN_DIALOG.NAME_LABEL') }}
        </label>
        <input
          v-model="form.name"
          type="text"
          class="reset-base w-full border rounded-lg px-3 py-2 text-sm"
          :class="error ? 'border-r-500' : 'border-n-weak'"
        />
        <p v-if="error" class="mt-1 text-xs text-r-500">
          {{ error }}
        </p>
      </div>

      <!-- Valid From / To -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ t('PRICING.PLAN_DIALOG.VALID_FROM_LABEL') }}
          </label>
          <input
            v-model="form.valid_from"
            type="date"
            class="reset-base w-full border border-n-weak rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ t('PRICING.PLAN_DIALOG.VALID_TO_LABEL') }}
          </label>
          <input
            v-model="form.valid_to"
            type="date"
            class="reset-base w-full border border-n-weak rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>

      <!-- Repeat Yearly -->
      <div class="flex items-center gap-2">
        <input
          id="plan-repeat-yearly"
          v-model="form.repeat_yearly"
          type="checkbox"
          class="reset-base"
        />
        <label for="plan-repeat-yearly" class="text-sm text-n-slate-11">
          {{ t('PRICING.PLAN_DIALOG.REPEAT_YEARLY') }}
        </label>
      </div>

      <!-- Is Default -->
      <div class="flex items-center gap-2">
        <input
          id="plan-is-default"
          v-model="form.is_default"
          type="checkbox"
          class="reset-base"
        />
        <label for="plan-is-default" class="text-sm text-n-slate-11">
          {{ t('PRICING.PLAN_DIALOG.IS_DEFAULT_LABEL') }}
        </label>
      </div>
    </div>
  </Dialog>
</template>
